import { useState } from 'react'
import SectionHeading from '../HomePage/SectionHeading'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/outline'
import Link from 'next/link'

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = []

    // Push visible page numbers (e.g. [1, 2, 3, ..., 9, 10])
    for (let i = 1; i <= totalPages; i++) {
        if (
            i === 1 ||
            i === totalPages ||
            (i >= currentPage - 1 && i <= currentPage + 1)
        ) {
            pages.push(i)
        } else if (i === currentPage - 2 || i === currentPage + 2) {
            pages.push('...')
        }
    }

    return (
        <div className="flex w-full justify-evenly space-x-2 sm:space-x-4 items-center text-olm-gray">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                className="px-4 sm:px-6 py-2 border border-gray-200 rounded-full flex space-x-2 items-center"
                disabled={currentPage === 1}>
                <ArrowLeftIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                <span className="text-sm sm:text-base">Previous</span>
            </button>

            <div className="flex-grow justify-center flex space-x-2 sm:space-x-4">
                {pages.map((page, idx) => (
                    <button
                        key={idx}
                        onClick={() =>
                            typeof page === 'number' && onPageChange(page)
                        }
                        className={`text-center py-2 font-semibold w-10 text-sm sm:text-base rounded ${
                            currentPage === page
                                ? 'bg-orange-500 text-white'
                                : 'text-olm-gray'
                        } `}>
                        {page}
                    </button>
                ))}
            </div>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 sm:px-6 py-2 border border-gray-200 rounded-full flex space-x-2 items-center">
                <span className="text-sm sm:text-base">Next</span>
                <ArrowRightIcon className="w-4 sm:w-5 h-4 sm:h-5" />
            </button>
        </div>
    )
}

function PodcastsWithPagination({
    podcasts,
    title,
    onPagechange,
    pagination = true,
}) {
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 12

    // Calculate the indices of the items to show on the current page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    const currentPodcasts =
        podcasts.length > 0
            ? podcasts.slice(indexOfFirstItem, indexOfLastItem)
            : []

    const totalPages = Math.ceil(podcasts.length / itemsPerPage)

    // Function to handle page change and scroll to top
    const handlePageChange = (page) => {
        setCurrentPage(page)
        if (onPagechange) {
            onPagechange(page)
        }
    }

    return (
        <div className="space-y-16">
            <SectionHeading>{title}</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
                {currentPodcasts?.length > 0 &&
                    currentPodcasts.map((podcast, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col space-y-4 sm:space-y-6">
                            <Link href={`/podcasts/${podcast.slug}`}>
                                <a className="space-y-4 sm:space-y-6">
                                    <img
                                        className="w-full h-auto block"
                                        src={
                                            podcast?.thumbnail?.url ||
                                            'https://via.placeholder.com/390x396?text=No+Image+Available'
                                        } // Fallback to online placeholder
                                        alt={
                                            podcast._embedded[
                                                'wp:featuredmedia'
                                            ][0].alt_text
                                        }
                                        title={
                                            podcast._embedded[
                                                'wp:featuredmedia'
                                            ][0].title.rendered
                                        }
                                    />

                                    <h3 className="text-black text-xl sm:text-2xl font-bold">
                                        {podcast?.title?.rendered}
                                    </h3>
                                </a>
                            </Link>
                            <p className="text-gray-500 h-24 overflow-hidden text-ellipsis">
                                <div
                                    className="line-clamp-4"
                                    dangerouslySetInnerHTML={{
                                        __html: podcast?.excerpt.rendered,
                                    }}
                                />
                            </p>
                            <Link href={`/podcasts/${podcast.slug}`}>
                                <a className="w-full sm:w-60 px-4 sm:px-6 py-3 sm:py-4 text-gray-600 border border-black rounded-full flex items-center space-x-2">
                                    <svg
                                        className="w-5 sm:w-6 h-5 sm:h-6 text-olh"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor">
                                        <path
                                            fillRule="evenodd"
                                            d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="uppercase text-sm sm:text-base">
                                        Play Episode
                                    </span>
                                </a>
                            </Link>
                        </div>
                    ))}
            </div>

            <div className="pt-10">
                {pagination && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    )
}

export default PodcastsWithPagination
